const conversionForClassName = {
  0: '0-0',
  0.13: '1-8',
  0.25: '1-4',
  0.38: '3-8',
  0.5: '1-2',
  0.63: '5-8',
  0.75: '3-4'
};
const conversionForText = {
  0: '',
  0.13: '⅛',
  0.25: '¼',
  0.38: '⅜',
  0.5: '½',
  0.63: '⅝',
  0.75: '¾'
};
export const convertSizeToClass = (str) => {
  const num = Number(str);
  const wholeNumber = Math.floor(num).toString();
  return `${wholeNumber}-${conversionForClassName[num - Math.floor(num)]}`;
};

export const convertDiameterToText = (diameter) => {
  const numDiameter = Number(diameter);
  if (Math.floor(numDiameter) > 0) {
    return `${Math.floor(numDiameter)}${
      conversionForText[numDiameter - Math.floor(numDiameter)]
    }"`;
  } else {
    return `${conversionForText[numDiameter]}"`;
  }
};

// A slender tube whose natural fitted pixel height would be shorter than this
// fraction of the canvas height is too short to design on. When that happens the
// tube switches to horizontal-scroll mode and is drawn at exactly this fraction of
// the canvas height (consistently), with its true length overflowing the viewport.
// This single knob controls both WHEN scroll engages and HOW TALL the tube is once
// it does: larger = engages sooner, taller tube, more scroll.
export const OVERFLOW_TUBE_HEIGHT_FRACTION = 0.2;

// The renderer flattens the tube using a half-circumference factor (pi / 2).
const TUBE_FLATTEN = 3.14 / 2;

// Safety bound (canvas coordinate px) on how wide a slender tube may be drawn so
// the enlarged Stage and print-composite buffers stay within the browser <canvas>
// ~16384px limit (the buffer renders at ~this * devicePixelRatio). At the target
// height this only binds on unusually tall canvases, so in practice the tube is
// drawn at the full target height.
const MAX_OVERFLOW_TUBE_WIDTH = 5000;

export const extractCanvasMeasurementsFromDesign = (design) => {
  const canvasHeight = design.canvasHeight ?? 0;
  // The visible canvas width. When the tube overflows for horizontal scroll the
  // returned `canvasWidth` grows beyond this, but every fit decision below is made
  // against the viewport so the result is stable regardless of how wide the
  // scrollable Stage ends up (keeps the print composite crop consistent).
  const viewportWidth = design.canvasWidth ?? 0;
  const canvasRatio = viewportWidth / canvasHeight;
  const padding = 30;
  const tubeAspectRatio = design.length / (design.diameter * TUBE_FLATTEN);

  // The pixel height the tube would get if it simply fit inside the viewport
  // (computed from the fit math, not measured from the DOM): width-constrained for
  // slender tubes, height-constrained otherwise.
  const naturalTubeHeight =
    tubeAspectRatio > canvasRatio
      ? (viewportWidth - padding * 2) / tubeAspectRatio
      : canvasHeight - padding * 2;

  // Too slender to design on if that natural height is shorter than the target
  // fraction of the canvas height (guarding against a zero/undersized canvas).
  const targetOverflowHeight = canvasHeight * OVERFLOW_TUBE_HEIGHT_FRACTION;
  const isOverflow = canvasHeight > 0 && naturalTubeHeight < targetOverflowHeight;

  let tubeWidth = 1;
  let tubeHeight = 1;
  if (isOverflow) {
    // Draw the tube at a consistent fraction of the canvas height; its true length
    // overflows the viewport and drives the horizontal scroll. Height derives from
    // canvasHeight, and the print composite re-derives geometry with the same
    // viewport width, so its crop matches what was rendered. The width safety cap
    // only binds on unusually tall canvases.
    tubeHeight = Math.min(targetOverflowHeight, MAX_OVERFLOW_TUBE_WIDTH / tubeAspectRatio);
    tubeWidth = tubeHeight * tubeAspectRatio;
  } else if (tubeAspectRatio > canvasRatio) {
    tubeWidth = viewportWidth - padding * 2;
    tubeHeight = tubeWidth / tubeAspectRatio;
  } else {
    tubeHeight = canvasHeight - padding * 2;
    tubeWidth = tubeHeight * tubeAspectRatio;
  }

  let ellipseRX = tubeWidth * 0.05;
  tubeWidth = tubeWidth + ellipseRX;

  // Safety re-fit that keeps the tube inside the viewport. Skip it while we are
  // intentionally overflowing, otherwise it would shrink the tube back to fit and
  // defeat the horizontal scroll.
  if (!isOverflow && tubeWidth / tubeHeight > canvasRatio) {
    const adjustedWidth = viewportWidth - padding * 2;

    const scaleFactor = adjustedWidth / (tubeWidth + ellipseRX * 2);
    tubeWidth = tubeWidth * scaleFactor;
    tubeHeight = tubeHeight * scaleFactor;
    ellipseRX = ellipseRX * scaleFactor;
  }

  // Coordinate-space / Stage width. When overflowing, widen it to contain the tube
  // AND both end-cap ellipses (radius ellipseRX each) with the same `padding` on
  // each side that a fitted tube gets — otherwise the caps get clipped at the left
  // edge and spill past the scrollable width. Otherwise it stays equal to the
  // viewport (unchanged behavior). The inset below then centers to ellipseRX.
  const canvasWidth = isOverflow ? tubeWidth + ellipseRX * 2 + padding * 2 : viewportWidth;

  const inset = (canvasWidth - tubeWidth) / 2 - padding;
  const drawableWidth = tubeWidth - ellipseRX;

  return {
    canvasHeight: canvasHeight,
    canvasWidth: canvasWidth,
    viewportWidth: viewportWidth,
    isOverflow: isOverflow,
    padding: padding,
    tubeWidth: tubeWidth,
    tubeHeight: tubeHeight,
    inset: inset,
    tubeAspectRatio: tubeAspectRatio,
    canvasRatio: canvasRatio,
    ellipseRX: ellipseRX,
    drawableWidth: drawableWidth,
    drawableHeight: tubeHeight
  };
};

export const extractMeasurementsFromCanvasElement = (
  canvasElement,
  design,
  devicePixelRatio,
  viewportWidth
) => {
  const renderedWidth = canvasElement.width / devicePixelRatio;
  const renderedHeight = canvasElement.height / devicePixelRatio;
  return extractCanvasMeasurementsFromDesign({
    diameter: design.diameter,
    length: design.length,
    canvasHeight: renderedHeight,
    // In horizontal-scroll (overflow) mode the rendered canvas element is wider
    // than the visible viewport, so the fit must use the viewport width when it
    // is supplied. Falling back to the element width preserves prior behavior.
    canvasWidth: viewportWidth ?? renderedWidth
  });
};
